const ALERTAS_API_BASE = "https://alerta-maceio-api-production.up.railway.app/api";
const PLACES_KEY = "vigial_saved_places_v1";
const SENSITIVITY_KEY = "vigial_alert_sensitivity_v1";
const PUSH_ENABLED_KEY = "vigial_push_enabled_v1";
const FREE_PLACE_LIMIT = 1;

function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem("vigial_device_id");
    if (!id) {
      id = window.generateStrongDeviceId ? window.generateStrongDeviceId() : "d" + Date.now() + Math.random().toString(36).slice(2);
      localStorage.setItem("vigial_device_id", id);
    }
    return id;
  } catch (e) { return "anon"; }
}
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function loadPlaces() {
  try { return JSON.parse(localStorage.getItem(PLACES_KEY) || "[]"); } catch (e) { return []; }
}
function savePlacesToStorage(places) {
  try { localStorage.setItem(PLACES_KEY, JSON.stringify(places)); } catch (e) {}
  if (window.pushAccountSync) window.pushAccountSync({ places });
}
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
window.PLACES_KEY = PLACES_KEY;
window.haversineKm = haversineKm;

function PlacePickerMap({ lat, lng, radius, onPick }) {
  const mapDivRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const circleRef = React.useRef(null);
  const onPickRef = React.useRef(onPick);
  onPickRef.current = onPick;

  React.useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, touchZoom: true }).setView([lat, lng], 14);
    L.tileLayer(window.VIGI_TILE_URL, window.VIGI_TILE_OPTIONS).addTo(map);
    const icon = L.divIcon({ className: "", html: '<div class="vigi-map-pin" style="background:var(--color-brand-primary)"></div>', iconSize: [13, 13], iconAnchor: [7, 7] });
    const marker = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
    const circle = L.circle([lat, lng], { radius, color: "var(--color-brand-primary)", weight: 1.5, fillColor: "var(--color-brand-primary)", fillOpacity: 0.12 }).addTo(map);
    marker.on("dragend", () => { const pos = marker.getLatLng(); circle.setLatLng(pos); onPickRef.current(pos.lat, pos.lng); });
    map.on("click", (e) => { marker.setLatLng(e.latlng); circle.setLatLng(e.latlng); onPickRef.current(e.latlng.lat, e.latlng.lng); });
    mapRef.current = map; markerRef.current = marker; circleRef.current = circle;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  React.useEffect(() => {
    if (circleRef.current) circleRef.current.setRadius(radius);
  }, [radius]);

  React.useEffect(() => {
    if (!markerRef.current || !mapRef.current) return;
    const current = markerRef.current.getLatLng();
    if (Math.abs(current.lat - lat) > 1e-6 || Math.abs(current.lng - lng) > 1e-6) {
      markerRef.current.setLatLng([lat, lng]);
      circleRef.current.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], 15);
    }
  }, [lat, lng]);

  return <div ref={mapDivRef} style={{ height: 140, borderRadius: 9, overflow: "hidden", position: "relative", zIndex: 0, border: "1px solid var(--color-border)" }} />;
}

function toneColorFor(catId) {
  const cat = (window.CATS || []).find((c) => c.id === catId);
  const tone = cat ? cat.tone : "neutral";
  return tone === "alert" ? "var(--color-alert)" : tone === "warning" ? "var(--color-warning)" : "var(--color-text-secondary)";
}

function NearbyOccurrencesMap({ lang, place, nearby, nearbyNews }) {
  const mapDivRef = React.useRef(null);

  React.useEffect(() => {
    if (!mapDivRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, touchZoom: true, attributionControl: false });
    L.tileLayer(window.VIGI_TILE_URL, window.VIGI_TILE_OPTIONS).addTo(map);
    L.circle([place.lat, place.lng], { radius: place.radius, color: "var(--color-brand-primary)", weight: 1.5, fillColor: "var(--color-brand-primary)", fillOpacity: 0.08 }).addTo(map);
    const bounds = [[place.lat, place.lng]];
    const badgeHtml = window.badgeHtml || ((text) => text);
    const offsets = window.spreadOverlappingPoints ? window.spreadOverlappingPoints(nearby, (r) => r.lat, (r) => r.lng) : nearby.map(() => [0, 0]);
    nearby.forEach((r, idx) => {
      const cat = (window.CATS || []).find((c) => c.id === r.category) || (window.CATS || []).find((c) => c.id === "outro");
      const iconUrl = window.catIconUrl ? window.catIconUrl(cat ? cat.icon : "more-horizontal") : "";
      const [px, py] = offsets[idx];
      const icon = L.divIcon({
        className: "",
        html: `<div class="vigi-map-pin" style="background:${toneColorFor(r.category)};transform:translate(${px}px,${py}px)"><span style="display:inline-block;width:11px;height:11px;background-color:#fff;-webkit-mask-image:url('${iconUrl}');mask-image:url('${iconUrl}');-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;"></span></div>`,
        iconSize: [20, 20], iconAnchor: [10, 10],
      });
      const safeBairro = window.escapeHtml(r.bairro || "");
      const popupHtml = `${badgeHtml(t(lang, "sourceCommunity"))}<br><b>${cat ? t(lang, cat.key) : window.escapeHtml(r.category)}</b><br>${safeBairro}<br>${window.timeAgoLabel ? window.timeAgoLabel(r.createdAt, lang) : ""} · ${r.confirms || 0}`;
      L.marker([r.lat, r.lng], { icon }).addTo(map).bindPopup(popupHtml);
      bounds.push([r.lat, r.lng]);
    });
    (nearbyNews || []).forEach((n) => {
      const icon = L.divIcon({
        className: "",
        html: `<div class="vigi-map-pin" style="background:var(--color-brand-primary)"><span style="display:inline-block;width:11px;height:11px;background-color:#fff;-webkit-mask-image:url('https://unpkg.com/lucide-static@0.462.0/icons/newspaper.svg');mask-image:url('https://unpkg.com/lucide-static@0.462.0/icons/newspaper.svg');-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;"></span></div>`,
        iconSize: [20, 20], iconAnchor: [10, 10],
      });
      const safeOutlet = window.escapeHtml(n.outlet || "");
      const safeTitle = window.escapeHtml(n.title || "");
      const safeLink = window.escapeHtml(n.link || "");
      const popupHtml = `${badgeHtml(safeOutlet)}<br><b>${safeTitle}</b><br><a href="${safeLink}" data-link="${safeLink}" onclick="window.vigiOpenNewsArticle(this.getAttribute('data-link'));return false;" rel="noreferrer">${t(lang, "readFullArticleAt")} ${safeOutlet} →</a>`;
      L.marker([n.lat, n.lng], { icon }).addTo(map).bindPopup(popupHtml);
      bounds.push([n.lat, n.lng]);
    });
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
    return () => map.remove();
  }, []);

  return <div ref={mapDivRef} style={{ height: 140, borderRadius: 9, overflow: "hidden", position: "relative", zIndex: 0, border: "1px solid var(--color-border)" }} />;
}

function PlaceCard({ lang, place, nearby, nearbyNews, onDelete }) {
  const { Icon } = window.VigiALDesignSystem_ff2dec;
  const [expanded, setExpanded] = React.useState(false);
  const newsItems = nearbyNews || [];
  const count = nearby.length + newsItems.length;
  return (
    <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "10px 12px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "var(--text-base)", fontWeight: 600 }}>{place.name}</div>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}>
            {place.radius}{t(lang, "meters")} · {count} {t(lang, "occurrencesInArea")}
          </div>
        </div>
        <button onClick={() => onDelete(place.id)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-muted)", flexShrink: 0 }}>
          <Icon name="trash-2" size={16} color="var(--color-text-muted)" />
        </button>
      </div>
      {count > 0 && (
        <button onClick={() => setExpanded((v) => !v)} style={{ marginTop: 6, background: "transparent", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--text-xs)", color: "var(--color-brand-primary)" }}>
          <Icon name="map-pin" size={11} color="var(--color-brand-primary)" /> {expanded ? t(lang, "hideOccurrences") : t(lang, "viewOccurrences")}
        </button>
      )}
      {expanded && count > 0 && (
        <div style={{ marginTop: 8 }}>
          <NearbyOccurrencesMap lang={lang} place={place} nearby={nearby} nearbyNews={newsItems} />
          <div style={{ marginTop: 4 }}>
            {nearby.map((r) => {
              const cat = (window.CATS || []).find((c) => c.id === r.category);
              return (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", padding: "5px 0", borderTop: "1px solid var(--color-border)" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: toneColorFor(r.category), flexShrink: 0 }} />
                  <span style={{ fontWeight: "var(--weight-semibold)", color: "var(--color-brand-deep)", background: "var(--color-brand-tint)", borderRadius: "var(--radius-pill)", padding: "1px 6px", flexShrink: 0 }}>{t(lang, "sourceCommunity")}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>{cat ? t(lang, cat.key) : r.category}{r.bairro ? ` · ${r.bairro}` : ""}</span>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--color-text-muted)", flexShrink: 0 }}>{window.timeAgoLabel(r.createdAt, lang)}</span>
                </div>
              );
            })}
            {newsItems.map((n) => (
              <div key={n.link} style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", padding: "5px 0", borderTop: "1px solid var(--color-border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--color-brand-primary)", flexShrink: 0 }} />
                  <span style={{ fontWeight: "var(--weight-semibold)", color: "var(--color-brand-deep)", background: "var(--color-brand-tint)", borderRadius: "var(--radius-pill)", padding: "1px 6px", flexShrink: 0 }}>{n.outlet}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.title}</span>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--color-text-muted)", flexShrink: 0 }}>{window.timeAgoLabel(n.publishedAt, lang)}</span>
                </div>
                <a href={n.link} onClick={(e) => { e.preventDefault(); window.vigiOpenNewsArticle(n.link); }} target="_blank" rel="noreferrer" style={{ marginLeft: 13, color: "var(--color-brand-primary)" }}>{t(lang, "readFullArticleAt")} {n.outlet}</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlertasScreen({ lang }) {
  const { Button, Icon } = window.VigiALDesignSystem_ff2dec;
  const [places, setPlaces] = React.useState(loadPlaces);
  const displayPlaces = places.filter((p) => !p.isBairro);
  const [premium, setPremiumFlag] = React.useState(() => (window.isPremium ? window.isPremium() : false));
  const [sensitivity, setSensitivity] = React.useState(() => {
    try { return localStorage.getItem(SENSITIVITY_KEY) || "all"; } catch (e) { return "all"; }
  });
  const [reports, setReports] = React.useState([]);
  const [news, setNews] = React.useState([]);
  const [adding, setAdding] = React.useState(false);
  const [nameErr, setNameErr] = React.useState(false);
  const [draftName, setDraftName] = React.useState("");
  const [draftLocationQuery, setDraftLocationQuery] = React.useState("");
  const [draftPos, setDraftPos] = React.useState({ lat: -9.605, lng: -35.745 });
  const [draftRadius, setDraftRadius] = React.useState(500);
  const [monitorStatus, setMonitorStatus] = React.useState("off");
  const [localAlert, setLocalAlert] = React.useState(null);
  const [pushStatus, setPushStatus] = React.useState(() => {
    try { return localStorage.getItem(PUSH_ENABLED_KEY) === "true" ? "on" : "off"; } catch (e) { return "off"; }
  });
  const [pushDebugStep, setPushDebugStep] = React.useState("");
  const watchIdRef = React.useRef(null);
  const insideRef = React.useRef({});
  const placesRef = React.useRef(places);
  const reportsRef = React.useRef(reports);
  const newsRef = React.useRef(news);
  const sensitivityRef = React.useRef(sensitivity);
  placesRef.current = places; reportsRef.current = reports; newsRef.current = news; sensitivityRef.current = sensitivity;

  React.useEffect(() => {
    fetch(`${ALERTAS_API_BASE}/reports?window=90d`).then((r) => r.json()).then(setReports).catch(() => {});
    fetch(`${ALERTAS_API_BASE}/news?window=90d`).then((r) => r.json()).then(setNews).catch(() => {});
  }, []);

  React.useEffect(() => {
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  function changeSensitivity(v) {
    setSensitivity(v);
    try { localStorage.setItem(SENSITIVITY_KEY, v); } catch (e) {}
  }

  function nearbyList(place, reportsList, sens) {
    return reportsList.filter((r) => {
      if (sens === "confirmed" && !(r.confirms > 0)) return false;
      return haversineKm(place.lat, place.lng, r.lat, r.lng) * 1000 <= place.radius;
    });
  }
  function nearbyNewsList(place, newsList) {
    return newsList.filter((n) => n.lat != null && n.lng != null && haversineKm(place.lat, place.lng, n.lat, n.lng) * 1000 <= place.radius);
  }
  function countNear(place, reportsList, newsList, sens) {
    return nearbyList(place, reportsList, sens).length + nearbyNewsList(place, newsList).length;
  }

  function triggerAlert(place, count) {
    const msg = `${t(lang, "alertTriggeredTitle")} ${place.name}: ${count} ${t(lang, "alertTriggeredBody")}`;
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification(place.name, { body: msg }); } catch (e) {}
    }
    setLocalAlert(msg);
    setTimeout(() => setLocalAlert(null), 6000);
  }

  function toggleMonitoring() {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setMonitorStatus("off");
      return;
    }
    if (!navigator.geolocation) { setMonitorStatus("denied"); return; }
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission();
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setMonitorStatus("on");
        const { latitude, longitude } = pos.coords;
        placesRef.current.forEach((p) => {
          const distM = haversineKm(latitude, longitude, p.lat, p.lng) * 1000;
          const inside = distM <= p.radius;
          const wasInside = !!insideRef.current[p.id];
          if (inside && !wasInside) {
            const count = countNear(p, reportsRef.current, newsRef.current, sensitivityRef.current);
            if (count > 0) triggerAlert(p, count);
          }
          insideRef.current[p.id] = inside;
        });
      },
      () => setMonitorStatus("denied"),
      { enableHighAccuracy: false, maximumAge: 15000, timeout: 10000 }
    );
  }

  async function syncPushSubscription(currentPlaces, sens) {
    try {
      if (!("serviceWorker" in navigator)) return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      await fetch(`${ALERTAS_API_BASE}/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId: getOrCreateDeviceId(), subscription: sub.toJSON(), places: currentPlaces, sensitivity: sens }),
      });
    } catch (e) {}
  }

  async function enablePush() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setPushStatus("unsupported"); return; }
    setPushStatus("loading");
    // The whole "loading" state used to be able to hang forever if any step
    // (SW registration, the permission prompt, or the subscribe call itself)
    // never resolved — happened in practice, with no way to tell which step
    // it was stuck on. Each step now has its own timeout, and the step name
    // is kept around so an error message can name where it actually failed.
    function withTimeout(promise, ms, step) {
      return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(Object.assign(new Error("timeout"), { step })), ms)),
      ]);
    }
    let step = "sw-register";
    try {
      const reg = await withTimeout(navigator.serviceWorker.register("sw.js"), 8000, step);
      step = "permission";
      setPushDebugStep(step);
      const permission = await withTimeout(Notification.requestPermission(), 30000, step);
      if (permission !== "granted") { setPushStatus("denied"); setPushDebugStep(""); return; }
      step = "vapid-key";
      setPushDebugStep(step);
      const keyRes = await withTimeout(fetch(`${ALERTAS_API_BASE}/push/vapid-public-key`), 8000, step);
      const keyData = await keyRes.json();
      step = "subscribe";
      setPushDebugStep(step);
      const sub = await withTimeout(reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
      }), 10000, step);
      step = "post-subscribe";
      setPushDebugStep(step);
      await withTimeout(fetch(`${ALERTAS_API_BASE}/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId: getOrCreateDeviceId(), subscription: sub.toJSON(), places, sensitivity }),
      }), 8000, step);
      try { localStorage.setItem(PUSH_ENABLED_KEY, "true"); } catch (e) {}
      setPushDebugStep("");
      setPushStatus("on");
    } catch (e) {
      setPushDebugStep((e && e.step) || step);
      setPushStatus("error");
    }
  }

  async function disablePush() {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
      await fetch(`${ALERTAS_API_BASE}/push/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId: getOrCreateDeviceId() }),
      });
    } catch (e) {}
    try { localStorage.setItem(PUSH_ENABLED_KEY, "false"); } catch (e) {}
    setPushStatus("off");
  }

  function togglePush() {
    if (pushStatus === "on") disablePush();
    else enablePush();
  }

  React.useEffect(() => {
    if (pushStatus === "on") syncPushSubscription(places, sensitivity);
  }, [places, sensitivity]);

  function handleAddPlace() {
    if (!draftName.trim()) { setNameErr(true); return; }
    const newPlace = { id: "p" + Date.now(), name: draftName.trim(), lat: draftPos.lat, lng: draftPos.lng, radius: draftRadius };
    const updated = [...places, newPlace];
    setPlaces(updated);
    savePlacesToStorage(updated);
    setAdding(false); setDraftName(""); setDraftLocationQuery(""); setNameErr(false); setDraftPos({ lat: -9.605, lng: -35.745 }); setDraftRadius(500);
  }
  function handleDelete(id) {
    const updated = places.filter((p) => p.id !== id);
    setPlaces(updated);
    savePlacesToStorage(updated);
  }

  return (
    <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 10 }}>{t(lang, "alertsIntro")}</div>

      <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 12 }}>
        <button onClick={toggleMonitoring} style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)" }}>
            <Icon name="radar" size={15} color={monitorStatus === "on" ? "var(--color-brand-primary)" : "var(--color-text-muted)"} />
            {t(lang, "monitorToggle")}
          </span>
          <span style={{
            width: 36, height: 20, borderRadius: 999, background: monitorStatus === "on" ? "var(--color-brand-primary)" : "var(--color-border)",
            position: "relative", flexShrink: 0, transition: "background .15s",
          }}>
            <span style={{ position: "absolute", top: 2, left: monitorStatus === "on" ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
          </span>
        </button>
        <div style={{ fontSize: "var(--text-xs)", color: monitorStatus === "denied" ? "var(--color-alert)" : "var(--color-text-muted)", marginTop: 6 }}>
          {monitorStatus === "on" ? t(lang, "monitoringOn") : monitorStatus === "denied" ? t(lang, "monitoringDenied") : t(lang, "monitoringOff")}
        </div>
      </div>

      <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 12 }}>
        <button onClick={premium ? togglePush : undefined} disabled={!premium || pushStatus === "loading"} style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent", border: "none",
          cursor: premium ? "pointer" : "not-allowed", fontFamily: "var(--font-body)",
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)" }}>
            <Icon name="bell-ring" size={15} color={pushStatus === "on" ? "var(--color-brand-primary)" : "var(--color-text-muted)"} />
            {t(lang, "pushToggle")}
            {!premium && <Icon name="lock" size={11} color="var(--color-text-muted)" />}
          </span>
          <span style={{
            width: 36, height: 20, borderRadius: 999, background: pushStatus === "on" ? "var(--color-brand-primary)" : "var(--color-border)",
            position: "relative", flexShrink: 0, transition: "background .15s", opacity: premium ? 1 : 0.5,
          }}>
            <span style={{ position: "absolute", top: 2, left: pushStatus === "on" ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
          </span>
        </button>
        <div style={{ fontSize: "var(--text-xs)", color: pushStatus === "denied" || pushStatus === "error" ? "var(--color-alert)" : "var(--color-text-muted)", marginTop: 6 }}>
          {!premium ? t(lang, "pushPremiumOnly")
            : pushStatus === "on" ? t(lang, "pushOn")
            : pushStatus === "loading" ? t(lang, "pushLoading")
            : pushStatus === "denied" ? t(lang, "pushDenied")
            : pushStatus === "unsupported" ? t(lang, "pushUnsupported")
            : pushStatus === "error" ? `${t(lang, "pushError")}${pushDebugStep ? ` (${pushDebugStep})` : ""}`
            : t(lang, "pushOff")}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button onClick={() => changeSensitivity("all")} style={{
          flex: 1, fontSize: "var(--text-xs)", padding: "6px 8px", borderRadius: "var(--radius-pill)", cursor: "pointer", fontFamily: "var(--font-body)",
          border: `1px solid ${sensitivity === "all" ? "var(--color-brand-primary)" : "var(--color-border)"}`,
          background: sensitivity === "all" ? "var(--color-brand-tint)" : "transparent",
          color: sensitivity === "all" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
        }}>{t(lang, "sensitivityAll")}</button>
        <button onClick={() => changeSensitivity("confirmed")} style={{
          flex: 1, fontSize: "var(--text-xs)", padding: "6px 8px", borderRadius: "var(--radius-pill)", cursor: "pointer", fontFamily: "var(--font-body)",
          border: `1px solid ${sensitivity === "confirmed" ? "var(--color-brand-primary)" : "var(--color-border)"}`,
          background: sensitivity === "confirmed" ? "var(--color-brand-tint)" : "transparent",
          color: sensitivity === "confirmed" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
        }}>{t(lang, "sensitivityConfirmed")}</button>
      </div>

      {localAlert && (
        <div style={{ background: "var(--color-alert-tint)", border: "1px solid var(--color-alert)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 12, fontSize: "var(--text-sm)", color: "var(--color-alert)" }}>
          {localAlert}
        </div>
      )}

      {displayPlaces.length === 0 && !adding && (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)", marginBottom: 10 }}>{t(lang, "noPlacesYet")}</div>
      )}

      {displayPlaces.map((p) => (
        <PlaceCard key={p.id} lang={lang} place={p} nearby={nearbyList(p, reports, sensitivity)} nearbyNews={nearbyNewsList(p, news)} onDelete={handleDelete} />
      ))}

      {adding ? (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginTop: 4 }}>
          <input
            value={draftName}
            onChange={(e) => { setDraftName(e.target.value); setNameErr(false); }}
            placeholder={t(lang, "placeNamePlaceholder")}
            style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 8, borderRadius: 8, border: `1px solid ${nameErr ? "var(--color-alert)" : "var(--color-border)"}`, background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8 }}
          />
          <window.PlaceSuggestInput
            value={draftLocationQuery}
            onChangeText={(v) => setDraftLocationQuery(v)}
            onSelect={(s) => {
              setDraftLocationQuery(s.name);
              setDraftPos({ lat: s.lat, lng: s.lng });
              if (!draftName.trim()) setDraftName(s.name);
            }}
            placeholder={t(lang, "searchLocationPlaceholder")}
            style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 8, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8 }}
          />
          <PlacePickerMap lat={draftPos.lat} lng={draftPos.lng} radius={draftRadius} onPick={(lat, lng) => setDraftPos({ lat, lng })} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "8px 0", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}>
            <span>{t(lang, "radiusLabel")}</span>
            <span style={{ fontFamily: "var(--font-mono)" }}>{draftRadius}{t(lang, "meters")}</span>
          </div>
          <input type="range" min="100" max="2000" step="100" value={draftRadius} onChange={(e) => setDraftRadius(Number(e.target.value))} style={{ width: "100%", marginBottom: 10 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="ghost" onClick={() => { setAdding(false); setNameErr(false); setDraftLocationQuery(""); }} style={{ flex: 1 }}>{t(lang, "cancel")}</Button>
            <Button variant="primary" onClick={handleAddPlace} style={{ flex: 1 }}>{t(lang, "savePlace")}</Button>
          </div>
        </div>
      ) : premium || displayPlaces.length < FREE_PLACE_LIMIT ? (
        <Button variant="secondary" onClick={() => setAdding(true)} style={{ width: "100%" }} icon={(p) => <Icon name="plus" size={14} {...p} />}>
          {t(lang, "addPlace")}
        </Button>
      ) : (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: 10 }}>{t(lang, "freeLimitReachedAlertas")}</div>
          <Button variant="primary" onClick={() => { if (window.changeTab) window.changeTab("perfil"); }} style={{ width: "100%" }}>
            {t(lang, "upgradeButton")}
          </Button>
        </div>
      )}
    </div>
  );
}
window.AlertasScreen = AlertasScreen;
